import {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Card, CardContent, CircularProgress, Grid, Snackbar, Stack, TextField, Typography} from '@mui/material';
import {ICustomer} from '@mybake/ui/models/Customers/ICustomer';
import {DTO_STATE} from '@mybake/ui/common/dictionary';
import {customerAPI} from '../../services/CustomerServices';
import {getStyles} from './customerCommon';
import {CustomerValidator} from '../../validators/CustomerValidator';
import {FuncButtonWIcon} from '../common/buttons/FunctionalButton';
import {MuiTelInput} from 'mui-tel-input';
import {getCustomerFieldName} from '@mybake/ui/common/helpers';
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {ICustomerGuid} from '@mybake/ui/models/Customers/ICustomerGuid';

interface CreateCustomerProps {
    handleCreateCustomerDialog: () => void;
    setCreatedCustomer?: (customer: ICustomer | ICustomerGuid) => void;
    initValue?: string;
}

const CreateCustomerNew = ({handleCreateCustomerDialog, setCreatedCustomer, initValue}: CreateCustomerProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [initRender, setInitRender] = useState(true);

    const {progressLoader, opacityHalf} = getStyles();
    const {t} = useAppTranslation();
    const [saveGuidCustomer, {
        error: saveGuidCustomerError,
        isSuccess: saveGuidCustomerIsSuccess,
        data: newGuidCustomerData,
    }] = customerAPI.useCreateCustomerV2Mutation();
    const saveCustomerError = saveGuidCustomerError;
    const saveCustomerIsSuccess = saveGuidCustomerIsSuccess;
    const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<'error' | 'success' | undefined>(undefined);
    const [isValidEmail, setIsValidEmail] = useState<'error' | 'success' | undefined>(undefined);
    const [isValidFirstName, setIsValidFirstName] = useState<'error' | 'success' | undefined>(undefined);
    const [isValidLastName, setIsValidLastName] = useState<'error' | 'success' | undefined>(undefined);
    const [isValidInstagram, setIsValidInstagram] = useState<'error' | 'success' | undefined>(undefined);
    const [isValidFacebook, setIsValidFacebook] = useState<'error' | 'success' | undefined>(undefined);
    const [existCustomerMessage, setExistCustomerMessage] = useState<string | undefined>();
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    const [newCustomer, setNewCustomer] = useState<ICustomer>({
        id: -1,
        firstName: '',
        lastName: '',
        name: '',
        email: '',
        phoneNumber: '',
        instagram: '',
        faceBook: '',
        state: DTO_STATE.DTO_NEW,
    });

    const validator = useMemo(() => CustomerValidator.getInstance(newCustomer), [newCustomer]);

    const handleCustomerFields = (e: any) => {
        setFieldErrors((previous) => ({...previous, [e.target.id]: '', name: ''}));
        setNewCustomer((prevState) => {
            return {
                ...prevState,
                [e.target.id]: e.target.value,
            };
        });
        // TODO: need to create common function to handle this typical steps
        if (e.target.id === 'email') {
            const isValid = validator.isValidEmail(e.target.value, true);
            setIsValidEmail(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }

        if (e.target.id === 'firstName') {
            const isValid = validator.isValidFirstName(e.target.value, true);
            setIsValidFirstName(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }

        if (e.target.id === 'lastName') {
            const isValid = validator.isValidLastName(e.target.value, true);
            setIsValidLastName(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }

        if (e.target.id === 'instagram') {
            const isValid = validator.isValidInstagram(e.target.value, true);
            setIsValidInstagram(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }

        if (e.target.id === 'faceBook') {
            const isValid = validator.isValidFacebook(e.target.value, true);
            setIsValidFacebook(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
    };

    const handlePhoneNumber = (phoneNumber: any) => {
        setFieldErrors((previous) => ({...previous, phone: '', phoneNumber: '', contact: ''}));
        const isValid = validator.isValidPhoneNumber(phoneNumber, true);
        setIsValidPhoneNumber(isValid === undefined ? undefined : isValid ? 'success' : 'error');

        setNewCustomer((prevState) => {
            return {
                ...prevState,
                phoneNumber: phoneNumber,
            };
        });
    };

    const handleCreateCustomer = () => {
        setIsLoading(true);
        saveGuidCustomer(newCustomer);
    };

    useEffect(() => {
        if (saveCustomerIsSuccess) {
            if (setCreatedCustomer !== undefined) {
                if (newGuidCustomerData) setCreatedCustomer(newGuidCustomerData);
            }
            handleCreateCustomerDialog();
            setIsLoading(false);
        }
    }, [saveCustomerIsSuccess, newGuidCustomerData, setCreatedCustomer, handleCreateCustomerDialog]);

    // TODO: need to add logic for disable save button if customer exist
    //       need to check prev values and if it will be same as before don't enable button
    useEffect(() => {
        setDisableSaveButton(!validator.validate());
        /*if (existCustomerMessage !== undefined) {
            let result = existCustomerMessage.includes(newCustomer.faceBook) || existCustomerMessage.includes(newCustomer.instagram);
        }*/
    }, [newCustomer]);

    useEffect(() => {
        if (saveCustomerError) {
            const errors = getApiFieldErrors(saveCustomerError, t);
            setFieldErrors(errors);
            const kind = getApiErrorKind(saveCustomerError);
            if (kind === 'server') {
                setServerToast(getApiErrorMessage(saveCustomerError, t));
                setTraceId(getApiTraceId(saveCustomerError));
                setExistCustomerMessage(undefined);
            } else {
                setExistCustomerMessage(Object.keys(errors).length ? undefined : getApiErrorMessage(saveCustomerError, t));
            }
            setIsLoading(false);
        }
    }, [saveCustomerError]);

    useEffect(() => {
        if (initRender) {
            setInitRender(false);
        } else {
            if (initValue) {
                const field = getCustomerFieldName(validator, initValue);
                if (field !== '') {
                    let tempValue = initValue;
                    if (field === 'phoneNumber') {
                        tempValue = initValue.replaceAll(' ', '');
                    }

                    setNewCustomer((prevState) => {
                        return {
                            ...prevState,
                            [field]: tempValue,
                        };
                    });
                }
            }
        }
    }, [initRender]);

    return (
        <Card sx={{mt: 2, position: 'relative'}}>
            {isLoading ? (
                <Box sx={progressLoader}>
                    <CircularProgress color="success"/>
                </Box>
            ) : (
                <></>
            )}

            <CardContent>
                <Stack spacing={3} sx={isLoading ? opacityHalf : {}}>
                    {existCustomerMessage && <Alert severity="error">{existCustomerMessage}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoFocus
                                id="firstName"
                                label={t('customers:first_name')}
                                focused={isValidFirstName !== undefined}
                                fullWidth
                                color={isValidFirstName}
                                variant="outlined"
                                value={newCustomer.firstName}
                                onChange={handleCustomerFields}
                                error={Boolean(fieldErrors.firstName || fieldErrors.name)}
                                helperText={fieldErrors.firstName || fieldErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="lastName"
                                label={t('customers:last_name')}
                                focused={isValidLastName !== undefined}
                                fullWidth
                                color={isValidLastName}
                                variant="outlined"
                                value={newCustomer.lastName}
                                onChange={handleCustomerFields}
                                error={Boolean(fieldErrors.lastName)} helperText={fieldErrors.lastName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="email"
                                label={t('customers:email')}
                                focused={isValidEmail !== undefined}
                                fullWidth
                                color={isValidEmail}
                                variant="outlined"
                                value={newCustomer.email}
                                onChange={handleCustomerFields}
                                error={Boolean(fieldErrors.email || fieldErrors.contact)} helperText={fieldErrors.email || fieldErrors.contact}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MuiTelInput
                                id="phone"
                                label={t('customers:phone_number')}
                                placeholder={t('customers:phone_number')}
                                focused={isValidPhoneNumber !== undefined}
                                fullWidth
                                color={isValidPhoneNumber}
                                variant="outlined"
                                value={newCustomer.phoneNumber}
                                onChange={handlePhoneNumber}
                                defaultCountry="PL"
                                onlyCountries={['UA', 'PL', 'DE', 'IE']}
                                forceCallingCode
                                error={Boolean(fieldErrors.phone || fieldErrors.phoneNumber || fieldErrors.contact)}
                                helperText={fieldErrors.phone || fieldErrors.phoneNumber || fieldErrors.contact}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="instagram"
                                label={t('customers:instagram')}
                                focused={isValidInstagram !== undefined}
                                fullWidth
                                color={isValidInstagram}
                                variant="outlined"
                                value={newCustomer.instagram}
                                onChange={handleCustomerFields}
                                error={Boolean(fieldErrors.instagram || fieldErrors.contact)} helperText={fieldErrors.instagram || fieldErrors.contact}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="faceBook"
                                label={t('customers:facebook')}
                                focused={isValidFacebook !== undefined}
                                fullWidth
                                color={isValidFacebook}
                                variant="outlined"
                                value={newCustomer.faceBook}
                                onChange={handleCustomerFields}
                                error={Boolean(fieldErrors.faceBook || fieldErrors.facebook || fieldErrors.contact)} helperText={fieldErrors.faceBook || fieldErrors.facebook || fieldErrors.contact}
                            />
                        </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end">
                        <FuncButtonWIcon
                            disabled={disableSaveButton}
                            field={t('common:create')}
                            handleClick={handleCreateCustomer}
                            iconName="sendIcon"
                        />
                    </Stack>
                </Stack>
            </CardContent>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Card>
    );
};

export default CreateCustomerNew;
